import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiSave, FiSearch, FiArrowLeft,
  FiCheck, FiXCircle, FiEye, FiFileText, FiPrinter, FiLink, FiLock, FiUnlock
} from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { getItemsCached, getCustomersCached, getEmployeesCached, getJobsCached, getLocationsCached } from '../../utils/masterDataStore';
import { confirmZeroPriceLines } from '../../utils/zeroPriceLines';
import { useAuth } from '../../context/AuthContext';
import { missingLocation, reqLabel, LOCATION_REQUIRED_MSG } from '../../utils/locationRequired';
import { formatAmount } from '../../utils/documentNumbering';
import { printDocument, loadDocumentFormats } from '../../utils/printTemplates';
import {
  createTransaction,
  updateTransaction,
  deleteTransaction,
  getTransactions,
  updateTransactionStatus,
  closeOrder,
  reopenOrder,
  orderPendingLines
} from '../../utils/transactionHelper';
import PageLayout from '../../components/PageLayout';
import SendWhatsAppButton from '../../components/SendWhatsAppButton';
import CustomerSelect from '../../components/CustomerSelect';
import ItemSelect from '../../components/ItemSelect';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useTableSort from '../../hooks/useTableSort';
import useDocListPagination from '../../hooks/useDocListPagination';
import DocListPagination from '../../components/DocListPagination';
import LinkedDocNumbers from '../../components/LinkedDocNumbers';
import ClosedOrderNote from '../../components/ClosedOrderNote';
import { isFutureDate, checkCustomerCreditLimit } from '../../utils/validationHelpers';
import { getItemStockCached } from '../../utils/stockFlowHelper';
import LineUnitPicker from '../../components/LineUnitPicker';
import TwoUnitQtyBox, { TwoUnitNote } from '../../components/TwoUnitQtyBox';
import { seedLineUnitFromItem, applyUnitChangeToLine, twoUnitSwitchQty } from '../../utils/unitConversion';
import { percentForDiscountAmount } from '../../utils/discountPercent';
import { loadIfEnabled as loadCustomerPricing, EMPTY_PRICING, filterItemsByPricing, getOverridePrice } from '../../utils/customerPricingHelper';
import { subCategoryLineDiscount } from '../../utils/customerSubCategoryDiscount';
import AttachmentPanel from '../../components/AttachmentPanel';
import {
  ActionBar, SearchBox, FilterGroup, StatusFilterSelect, TranAddButton, LoadingMessage,
  TranEmptyState, TableContainer, Table, Th, Tr, Td, StatusBadge, ActionGroup, IconBtn,
  SummaryBar, FormHeader, BackButton, FormTitle, FormActions, SaveButton, FormCard,
  SectionTitle, FormGrid, FormGroup, Label, Input, Select, TextArea, InfoGrid, InfoItem,
  NotesText, LineItemsHeader, AddLineButton, LineTableContainer, LineTable, LineTh, LineTd,
  LineInput, RemoveLineBtn, TotalsSection, TotalsTable, TotalRow, DiscountInput,
  SortableTh, SortContext
} from '../../components/SharedStyles';
import { inActiveBranch } from '../../utils/branchLink';
import { carryEmployeeJob, hsCodePatch } from '../../utils/linkCarry';

const TRAN_TYPE_CODE = 'SO';

// Footer summary spec — aggregated server-side in paged mode (~few reads),
// computed from the loaded list in filtered mode. Matches the old footer.
const SUMMARY_SPEC = {
  statuses: ['Draft', 'Approved', 'Completed']
};

const EMPTY_LINE = {
  itemId: '', itemCode: '', itemName: '', description: '', quantity: 1, unitOfMeasure: '', unitCode: '', unitPrice: 0,
  priceType: 'Retail', discountPercent: 0, discountAmount: 0, taxable: false,
  taxRate: 0, taxAmount: 0, thirdSchedule: false, lineTotal: 0,
  deliveredQty: 0, pendingQty: 0
};

const STATUS_COLORS = {
  Draft: colors.warning.main,
  Approved: colors.info.main,
  'Partially Delivered': '#9C27B0',
  Completed: colors.success.main,
  Closed: '#546E7A', // closed by hand with some quantity left (see Close Order)
  Cancelled: colors.error.main,
};

const SaleOrder = () => {
  const { activeCompany, activeFinancialYear, currentUser, companyUser, activeBranch, isModuleEnabled, userProfile } = useAuth();
  const priceClassEnabled = isModuleEnabled && isModuleEnabled('customer_class');
  const thirdScheduleEnabled = isModuleEnabled && isModuleEnabled('sales_third_schedule_tax');
  const { canEdit, canDelete, canCancel, canChange } = usePagePermissions('transactions', 'saleOrder');
  const showBranchCol = activeBranch && activeBranch.id === 'all';

  const [printConfig, setPrintConfig] = useState(null);
  useEffect(() => { if (activeCompany?.id) loadDocumentFormats(activeCompany.id).then(setPrintConfig); }, [activeCompany]);

  const isAdmin = companyUser?.role === 'admin';

  const location = useLocation();
  const [viewMode, setViewMode] = useState('list');
  const sort = useTableSort('docDate', 'desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  // Paginated list (2026-07-16, Firebase cost): 20 docs/page from Firestore.
  // Any search / status filter / custom column sort switches to a one-time
  // full active-year load so filtering behaves exactly as before.
  const listFiltering = searchQuery.trim() !== '' || statusFilter !== 'All'
    || sort.sortField !== 'docDate' || sort.sortDir !== 'desc';
  const {
    docs: orders,
    loading,
    refresh: loadOrders,
    ensureDoc,
    applyLocal,
    summary: listSummary,
    pagination
  } = useDocListPagination({
    tranTypeCode: TRAN_TYPE_CODE,
    fullMode: listFiltering,
    summarySpec: SUMMARY_SPEC
  });

  const [editingOrder, setEditingOrder] = useState(null);
  const [viewingOrder, setViewingOrder] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    docDate: new Date().toISOString().split('T')[0],
    partyId: '', partyName: '', parentId: '', parentDocNumber: '',
    customerPO: '', deliveryDate: '', deliveryAddress: '',
    currency: 'PKR', discountAmount: 0, notes: '', termsConditions: '', status: 'Draft',
    employeeId: '', employeeName: '', jobId: '', jobName: '',
    locationId: '', locationName: ''
  });
  const [lineItems, setLineItems] = useState([{ ...EMPTY_LINE }]);

  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [customerPricing, setCustomerPricing] = useState(EMPTY_PRICING);
  const [stockBalances, setStockBalances] = useState({});
  const [acceptedQuotations, setAcceptedQuotations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [locations, setLocations] = useState([]);

  // Order list now comes from useDocListPagination above (loadOrders = its
  // refresh). The old full-history getTransactions load lived here.

  // Tier 1 (2026-07-27, Firebase cost): customers/items come from the device-local
  // master cache (getMaster* → IndexedDB, 0 reads per open with a throttled
  // background change-check) instead of a full getDocs on every open. Each screen
  // keeps its own client-side filter/sort on the returned rows.
  const loadCustomers = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getCustomersCached(activeCompany.id);
      setCustomers([...rows].sort((a, b) => (a.companyName || '').localeCompare(b.companyName || '')));
    } catch (error) { console.error('Error loading customers:', error); }
  }, [activeCompany]);

  const loadItems = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getItemsCached(activeCompany.id);
      setItems(rows.filter(i => i.isDeleted !== true).sort((a, b) => (a.itemName || '').localeCompare(b.itemName || '')));
    } catch (error) { console.error('Error loading items:', error); }
  }, [activeCompany]);

  // ── D3 (2026-07-23, Firebase cost): the loader below feeds ONLY the form's
  // "Link to Quotation" dropdown — nothing on the list view uses it. It used to
  // run on every page open AND after every save, scanning full quotation
  // history (all Sent + all Accepted QTs ever). It now loads ONCE, lazily, the
  // first time a form is actually opened. List browsing and saving cost nothing.
  const formDataLoadedRef = useRef(false);

  // Load accepted quotations from unified transactions collection
  const loadAcceptedQuotations = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const sentQTs = await getTransactions(activeCompany.id, {
        tranTypeCode: 'QT',
        status: 'Sent'
      });
      const acceptedQTs = await getTransactions(activeCompany.id, {
        tranTypeCode: 'QT',
        status: 'Accepted'
      });
      setAcceptedQuotations([...sentQTs, ...acceptedQTs]);
    } catch (error) { console.error('Error loading quotations:', error); }
  }, [activeCompany]);

  // Loads the form-only dropdown data once per page session (see D3 note above).
  const ensureFormData = useCallback(async () => {
    if (formDataLoadedRef.current || !activeCompany?.id) return;
    formDataLoadedRef.current = true;
    try {
      await Promise.all([loadAcceptedQuotations()]);
    } catch (error) {
      formDataLoadedRef.current = false; // let a later form open retry
      console.error('Error loading form link data:', error);
    }
  }, [activeCompany, loadAcceptedQuotations]);

  const loadEmployees = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getEmployeesCached(activeCompany.id);
      setEmployees(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading employees:', error); }
  }, [activeCompany]);

  const loadJobs = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getJobsCached(activeCompany.id);
      setJobs(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading jobs:', error); }
  }, [activeCompany]);

  const loadLocations = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getLocationsCached(activeCompany.id);
      setLocations(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading locations:', error); }
  }, [activeCompany]);

  // Firebase cost: stock is fetched ONE ITEM AT A TIME, on demand. The old
  // mount-time load scanned the company's ENTIRE stockFlow history just to draw
  // badges the user may never look at. Now the dropdown shows a "stock?" chip
  // and this runs only when the user taps it.
  const requestItemStock = useCallback(async (itemId) => {
    if (!activeCompany?.id || !itemId) return;
    try {
      const qty = await getItemStockCached(activeCompany.id, itemId, {
        branchId: (activeBranch && activeBranch.id !== 'all') ? activeBranch.id : undefined
      });
      setStockBalances(prev => ({ ...prev, [itemId]: qty }));
    } catch (e) { console.error('Error loading item stock:', e); }
  }, [activeCompany, activeBranch]);

  // NOTE: the order list itself loads inside useDocListPagination — not here.
  // D3: the quotation loader is NOT here anymore — it loads lazily on first
  // form open (ensureFormData), since only the form uses it.
  useEffect(() => {
    loadCustomers(); loadItems(); loadEmployees(); loadJobs(); loadLocations();
  }, [loadCustomers, loadItems, loadEmployees, loadJobs, loadLocations]);

  const filteredOrders = useMemo(() => {
    let filtered = orders;
    if (statusFilter !== 'All') filtered = filtered.filter(o => o.status === statusFilter);
    if (searchQuery.trim()) {
      const sq = searchQuery.toLowerCase();
      filtered = filtered.filter(o =>
        o.docNumber?.toLowerCase().includes(sq) ||
        o.partyName?.toLowerCase().includes(sq) ||
        o.customerPO?.toLowerCase().includes(sq)
      );
    }
    return sort.sortItems(filtered);
  }, [orders, statusFilter, searchQuery, sort]);

  // 3rd Schedule mark of a line: the mark saved on the line; lines saved before
  // the mark was kept (no mark at all) take it from the item, like Sale Invoice.
  const lineThirdSchedule = (line) => (typeof line.thirdSchedule === 'boolean'
    ? line.thirdSchedule
    : items.find(i => i.id === line.itemId)?.thirdSchedule === true);

  // Lines opened before the item list arrived (rare): settle their 3rd Schedule
  // mark from the item once the list is here, so the totals use it too.
  useEffect(() => {
    if (!items.length) return;
    setLineItems(prev => (prev.some(l => l.itemId && typeof l.thirdSchedule !== 'boolean')
      ? prev.map(l => (l.itemId && typeof l.thirdSchedule !== 'boolean'
        ? { ...l, thirdSchedule: items.find(i => i.id === l.itemId)?.thirdSchedule === true }
        : l))
      : prev));
  }, [items]);

  const calculateLine = (line) => {
    const amount = (line.quantity || 0) * (line.unitPrice || 0);
    const discAmt = amount * ((line.discountPercent || 0) / 100);
    const net = amount - discAmt;
    // GST base: 3rd Schedule module ON → per item (3rd Schedule = full price
    // before discount, Normal = after discount); module OFF → unchanged (full price).
    const taxBase = thirdScheduleEnabled ? (lineThirdSchedule(line) ? amount : net) : amount;
    const tax = line.taxable ? taxBase * ((line.taxRate || 0) / 100) : 0;
    return {
      ...line,
      discountAmount: Math.round(discAmt * 100) / 100,
      taxAmount: Math.round(tax * 100) / 100,
      lineTotal: Math.round((net + tax) * 100) / 100
    };
  };

  const totals = useMemo(() => {
    const lines = lineItems.map(calculateLine);
    const subtotal = lines.reduce((s, l) => s + ((l.quantity || 0) * (l.unitPrice || 0) - (l.discountAmount || 0)), 0);
    const taxAmount = lines.reduce((s, l) => s + (l.taxAmount || 0), 0);
    const disc = Number(formData.discountAmount) || 0;
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      taxAmount: Math.round(taxAmount * 100) / 100,
      headerDiscount: disc,
      totalAmount: Math.round((subtotal - disc + taxAmount) * 100) / 100
    };
  }, [lineItems, formData.discountAmount]);

  const handleHeaderChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleCustomerChange = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    // Auto-fill the salesperson (employee) from the customer's saved default.
    // Cashier can still override the Employee dropdown per document.
    const spPatch = customer?.salesPersonId
      ? { employeeId: customer.salesPersonId, employeeName: customer.salesPersonName || '' }
      : {};
    setFormData(prev => ({ ...prev, partyId: customerId, partyName: customer?.companyName || '', parentId: '', parentDocNumber: '', ...spPatch }));
    setLineItems([{ ...EMPTY_LINE }]);
    if (customerId) {
      loadCustomerPricing(activeCompany?.id, customerId, isModuleEnabled)
        .then(setCustomerPricing)
        .catch(() => setCustomerPricing(EMPTY_PRICING));
    } else {
      setCustomerPricing(EMPTY_PRICING);
    }
  };

  const handleQuotationChange = (quotationId) => {
    const qt = acceptedQuotations.find(q => q.id === quotationId);
    if (!qt) {
      setFormData(prev => ({ ...prev, parentId: '', parentDocNumber: '' }));
      return;
    }
    setFormData(prev => ({
      ...prev, parentId: quotationId, parentDocNumber: qt.docNumber,
      partyId: qt.partyId, partyName: qt.partyName,
      ...carryEmployeeJob(qt)
    }));
    if (qt.lines?.length > 0) {
      setLineItems(qt.lines.map(l => ({
        ...EMPTY_LINE,
        itemId: l.itemId || '',
        itemCode: l.itemCode || '',
        itemName: l.itemName || '',
        description: l.description || '',
        ...hsCodePatch(l, items.find(i => i.id === l.itemId)),
        quantity: l.quantity || 0,
        unitOfMeasure: l.unitOfMeasure || '',
        unitPrice: l.unitPrice || 0,
        discountPercent: l.discountPercent || 0,
        taxable: l.taxable || false,
        taxRate: l.taxRate ?? 0,
        // the quotation line's 3rd Schedule mark (old quotations: from the item)
        thirdSchedule: typeof l.thirdSchedule === 'boolean' ? l.thirdSchedule : undefined
      })));
    }
  };

  const handleLineChange = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // Line discount can be entered as % OR a rupee amount (they sync), same as
      // the Sale Invoice. The % is the stored driver; typing an amount
      // back-calculates the % so totals stay exact.
      if (field === 'discountAmountInput') {
        const gross = (Number(updated[index].quantity) || 0) * (Number(updated[index].unitPrice) || 0);
        const amt = Math.max(0, Number(value) || 0);
        delete updated[index].discountAmountInput; // not a persisted field
        // Shortest % that gives back exactly the typed amount (4 decimals alone
        // could turn 70 into 69.99).
        updated[index].discountPercent = percentForDiscountAmount(gross, amt);
      }

      // Price type change - auto-fill unit price from item
      if (field === 'priceType' && updated[index].itemId) {
        const item = items.find(i => i.id === updated[index].itemId);
        if (item) {
          if (value === 'Retail') updated[index].unitPrice = item.salePrice || 0;
          else if (value === 'Wholesale') updated[index].unitPrice = item.wholesalePrice || 0;
          else if (value === 'Dealer') updated[index].unitPrice = item.dealerPrice || 0;
          const ovr = getOverridePrice(customerPricing, updated[index].itemId);
          if (ovr !== null) updated[index].unitPrice = ovr;
        }
      }

      if (field === 'itemId' && value) {
        const item = items.find(i => i.id === value);
        if (item) {
          updated[index].itemCode = item.itemCode || '';
          updated[index].itemName = item.itemName || '';
          updated[index].description = item.itemName || '';
          const cust = customers.find(c => c.id === formData.partyId);
          // Customer's fixed discount % (Customers master) seeds the line, same as
          // the Sale Invoice. Blank on the customer → line stays 0.
          if (cust && cust.defaultDiscountPercent != null && cust.defaultDiscountPercent !== '') {
            updated[index].discountPercent = Number(cust.defaultDiscountPercent) || 0;
          }
          // Customer Discount by Sub-Category (module): the item's sub-category %
          // for this customer wins; else the default above; else 0.
          const scd = subCategoryLineDiscount(cust, item, isModuleEnabled, cust ? cust.defaultDiscountPercent : null);
          if (scd !== undefined) updated[index].discountPercent = scd;
          // Customer Class Pricing: default the price tier from the customer's class.
          const cls = (priceClassEnabled && cust?.customerClass) || null;
          updated[index].priceType = cls || 'Retail';
          // Sales tax: the customer's own rate (Customers master), when set,
          // OVERRIDES the item's GST — same as the Sale Invoice. Customer 0 =
          // tax-exempt. Blank on the customer → item's own GST.
          const custTax = (cust && cust.defaultTaxRate != null && cust.defaultTaxRate !== '') ? Number(cust.defaultTaxRate) : null;
          if (custTax != null && Number.isFinite(custTax)) {
            updated[index].taxRate = custTax;
            updated[index].taxable = custTax > 0;
          } else {
            updated[index].taxRate = item.gstRate || 0;
            updated[index].taxable = (item.gstRate || 0) > 0;
          }
          updated[index].thirdSchedule = item.thirdSchedule === true;
          // HS Code from the item (carried on to the Challan → Invoice).
          updated[index].hsCode = item.hsCode || '';
          const seeded = seedLineUnitFromItem(item, { priceField: 'salePrice', defaultUnitField: 'defaultSaleUnit' });
          updated[index] = { ...updated[index], ...seeded };
          if (cls === 'Wholesale') updated[index].unitPrice = item.wholesalePrice || updated[index].unitPrice;
          else if (cls === 'Dealer') updated[index].unitPrice = item.dealerPrice || updated[index].unitPrice;
          const ovr = getOverridePrice(customerPricing, value);
          if (ovr !== null) updated[index].unitPrice = ovr;
        }
      }

      if (field === 'unitCode' && updated[index].itemId) {
        const item = items.find(i => i.id === updated[index].itemId);
        if (item) {
          const patch = applyUnitChangeToLine(item, value, { priceField: 'salePrice' });
          updated[index] = { ...updated[index], ...patch };
          const ovr = getOverridePrice(customerPricing, updated[index].itemId);
          if (ovr !== null) updated[index].unitPrice = ovr;
          // Show Two Units: switching "Rate per" between the item's two units keeps the same physical qty
          // (not on lines linked to another document — their qty is tracked against it).
          const twoQ = (formData.parentId || prev[index].sourceDocId || prev[index].poLineId) ? null
            : twoUnitSwitchQty(item, prev[index].unitCode || prev[index].unitOfMeasure, value, prev[index].quantity);
          if (twoQ !== null) updated[index].quantity = twoQ;
        }
      }

      updated[index] = calculateLine(updated[index]);
      return updated;
    });
  };

  const handlePriceBlur = (index) => {
    const line = lineItems[index];
    if (line.itemId && line.unitPrice > 0) {
      const item = items.find(i => i.id === line.itemId);
      if (item && item.minimumPrice > 0 && line.unitPrice < item.minimumPrice) {
        if (!window.confirm(`Price ${line.unitPrice} is below minimum price ${item.minimumPrice} for ${item.itemName}. Continue?`)) {
          setLineItems(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], unitPrice: item.minimumPrice };
            updated[index] = calculateLine(updated[index]);
            return updated;
          });
        }
      }
    }
  };

  const addLine = () => setLineItems(prev => [...prev, { ...EMPTY_LINE }]);

  const lineItemRefs = useRef([]);
  const pendingFocusRowRef = useRef(null);
  useEffect(() => {
    if (pendingFocusRowRef.current == null) return;
    const rowIdx = pendingFocusRowRef.current;
    pendingFocusRowRef.current = null;
    setTimeout(() => {
      const el = lineItemRefs.current[rowIdx];
      if (el && typeof el.focus === 'function') el.focus();
    }, 50);
  }, [lineItems.length]);

  const handleLastFieldEnter = (e, index) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (formData.parentId) return;
    const isLastRow = index === lineItems.length - 1;
    const line = lineItems[index];
    const hasData = !!line.itemId || (Number(line.quantity) > 0 && Number(line.unitPrice) > 0);
    if (!hasData) return;
    if (isLastRow) {
      // eslint-disable-next-line no-alert
      if (window.confirm('Add a new line item?')) {
        pendingFocusRowRef.current = lineItems.length;
        addLine();
      }
    } else {
      const next = lineItemRefs.current[index + 1];
      if (next && typeof next.focus === 'function') next.focus();
    }
  };
  const removeLine = (index) => { if (lineItems.length <= 1) return; setLineItems(prev => prev.filter((_, i) => i !== index)); };

  const resetForm = () => {
    setFormData({
      docDate: new Date().toISOString().split('T')[0],
      partyId: '', partyName: '', parentId: '', parentDocNumber: '',
      customerPO: '', deliveryDate: '', deliveryAddress: '',
      currency: activeCompany?.currencyCode || 'PKR', discountAmount: 0, notes: '', termsConditions: '', status: 'Draft',
      employeeId: '', employeeName: '', jobId: '', jobName: '',
      locationId: '', locationName: ''
    });
    setLineItems([{ ...EMPTY_LINE }]);
    setEditingOrder(null);
  };

  const handleCreate = () => { resetForm(); ensureFormData(); setViewMode('form'); };

  const handleEdit = (so) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    ensureFormData(); // D3: load quotation link data on demand
    setEditingOrder(so);
    setFormData({
      docDate: so.docDate || new Date().toISOString().split('T')[0],
      partyId: so.partyId || '', partyName: so.partyName || '',
      parentId: so.parentId || '', parentDocNumber: so.parentDocNumber || '',
      customerPO: so.customerPO || '', deliveryDate: so.deliveryDate || '',
      deliveryAddress: so.deliveryAddress || '', currency: so.currency || 'PKR',
      discountAmount: so.discountAmount || 0, notes: so.notes || '',
      termsConditions: so.termsConditions || '', status: so.status || 'Draft',
      employeeId: so.employeeId || '', employeeName: so.employeeName || '',
      jobId: so.jobId || '', jobName: so.jobName || '',
      locationId: so.locationId || '', locationName: so.locationName || ''
    });
    // Keep the line's own 3rd Schedule mark; an old line without one is resolved
    // from the item (see lineThirdSchedule) instead of defaulting to Normal.
    setLineItems(so.lines?.length > 0
      ? so.lines.map(l => ({ ...EMPTY_LINE, ...l, thirdSchedule: typeof l.thirdSchedule === 'boolean' ? l.thirdSchedule : undefined }))
      : [{ ...EMPTY_LINE }]);
    if (so.partyId) {
      loadCustomerPricing(activeCompany?.id, so.partyId, isModuleEnabled)
        .then(setCustomerPricing)
        .catch(() => setCustomerPricing(EMPTY_PRICING));
    } else {
      setCustomerPricing(EMPTY_PRICING);
    }
    setViewMode('form');
  };

  const handleView = (so) => { setViewingOrder(so); setViewMode('view'); };

  // Handle navigation from reports (e.g., report drill-down to a document #).
  // With pagination the target may not be in the loaded page — ensureDoc
  // fetches that single document by id when needed.
  useEffect(() => {
    const targetId = location.state?.viewTransactionId;
    if (!targetId) return;
    ensureDoc(targetId).then(transaction => {
      if (transaction) {
        setViewingOrder(transaction);
        setViewMode('view');
        // Clear the state to prevent re-triggering
        window.history.replaceState({}, document.title);
      }
    });
  }, [location.state, ensureDoc]);

  // Save order using transactionHelper
  const handleSave = async (saveStatus) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (missingLocation(activeCompany, formData.locationId)) { alert(LOCATION_REQUIRED_MSG); return; }
    if (!formData.partyId) { alert('Please select a customer'); return; }
    if (!activeFinancialYear?.id) { alert('Please select an active financial year'); return; }

    // Zero price allowed (free items) — negative prices still left out. See utils/zeroPriceLines.js.
    const validLines = lineItems.filter(l => l.description && l.quantity > 0 && Number(l.unitPrice) >= 0);
    if (validLines.length === 0) { alert('Please add at least one valid line item'); return; }
    if (!confirmZeroPriceLines(validLines)) return;

    // Discount cap validation (max 50%)
    for (const line of validLines) {
      if (Number(line.discountPercent) > 50) {
        alert(`Discount on "${line.itemName || line.description}" exceeds 50%. Please review.`); return;
      }
    }

    // Future date warning
    if (isFutureDate(formData.docDate)) {
      if (!window.confirm('Document date is in the future. Continue?')) return;
    }

    // Credit limit check when approving
    if (saveStatus === 'Approved' && formData.partyId) {
      const creditResult = await checkCustomerCreditLimit(activeCompany.id, formData.partyId, totals.totalAmount, editingOrder?.id);
      if (creditResult.exceeded) {
        if (!window.confirm(`Customer credit limit exceeded!\nLimit: ${creditResult.creditLimit.toLocaleString()}\nOutstanding: ${creditResult.outstanding.toLocaleString()}\nNew total: ${creditResult.newTotal.toLocaleString()}\n\nContinue anyway?`)) return;
      }
    }

    setSaving(true);
    try {
      const calculatedLines = validLines.map((l, i) => {
        const calc = calculateLine(l);
        return {
          lineNumber: i + 1,
          itemId: l.itemId,
          itemCode: l.itemCode || '',
          itemName: l.itemName || '',
          description: l.description,
          ...(l.hsCode ? { hsCode: l.hsCode } : {}),
          quantity: l.quantity,
          unitPrice: l.unitPrice,
          unitOfMeasure: l.unitOfMeasure || '',
          discountPercent: l.discountPercent || 0,
          discountAmount: calc.discountAmount,
          taxable: l.taxable,
          taxRate: l.taxRate,
          taxAmount: calc.taxAmount,
          thirdSchedule: lineThirdSchedule(l),
          lineTotal: calc.lineTotal,
          deliveredQty: l.deliveredQty || 0,
          pendingQty: l.quantity - (l.deliveredQty || 0)
        };
      });

      const status = saveStatus || formData.status || 'Draft';

      const transactionData = {
        tranTypeCode: TRAN_TYPE_CODE,
        docDate: formData.docDate,
        financialYearId: activeFinancialYear.id,
        partyType: 'customer',
        partyId: formData.partyId,
        partyName: formData.partyName,
        parentId: formData.parentId || null,
        parentDocNumber: formData.parentDocNumber || '',
        customerPO: formData.customerPO,
        deliveryDate: formData.deliveryDate,
        deliveryAddress: formData.deliveryAddress,
        currency: formData.currency,
        employeeId: formData.employeeId,
        employeeName: formData.employeeName,
        jobId: formData.jobId,
        jobName: formData.jobName,
        locationId: formData.locationId,
        locationName: formData.locationName,
        branchId: activeBranch?.id || null,
        branchName: activeBranch?.branchName || null,
        subtotal: totals.subtotal,
        discountAmount: totals.headerDiscount,
        taxAmount: totals.taxAmount,
        totalAmount: totals.totalAmount,
        notes: formData.notes,
        termsConditions: formData.termsConditions,
        status,
        lines: calculatedLines,
        ...(status === 'Approved' ? { approvedBy: currentUser.uid, approvedAt: new Date().toISOString() } : {})
      };

      let savedTransaction;
      let newTranData = null;
      let transactionId;

      if (editingOrder) {
        // Update existing
        savedTransaction = await updateTransaction(
          activeCompany.id,
          editingOrder.id,
          transactionData,
          currentUser.uid
        );

        // Handle status change
        if (status !== editingOrder.status) {
          await updateTransactionStatus(activeCompany.id, editingOrder.id, status, currentUser.uid);
          // approvedBy/approvedAt included in transactionData
        }
        transactionId = editingOrder.id;
      } else {
        // Create new
        newTranData = await createTransaction(
          activeCompany.id,
          transactionData,
          currentUser.uid
        );
        savedTransaction = newTranData;

        // Update status if not Draft
        if (status !== 'Draft') {
          await updateTransactionStatus(activeCompany.id, newTranData.id, status, currentUser.uid);
          // approvedBy/approvedAt included in transactionData
        }
        transactionId = newTranData.id;
      }

      // The quotation now has this order — it is not offered again.
      if (formData.parentId && !editingOrder && transactionId) {
        setAcceptedQuotations(prev => prev.map(q => (q.id === formData.parentId
          ? { ...q, childIds: [...(q.childIds || []), transactionId] } : q)));
      }

      // Update parent quotation status when SO is approved
      if (formData.parentId && status === 'Approved') {
        try {
          await updateTransactionStatus(activeCompany.id, formData.parentId, 'Accepted', currentUser.uid);
        } catch (err) { console.error('Error updating quotation:', err); }
      }

      // Optimistic UI update — show the saved record immediately so the Save
      // button releases without waiting on a full collection re-fetch.
      const savedRecord = editingOrder
        ? { ...editingOrder, ...transactionData, id: transactionId, status }
        : { ...newTranData, status };
      applyLocal(prev => editingOrder
        ? prev.map(r => r.id === transactionId ? savedRecord : r)
        : [savedRecord, ...prev]);

      setViewMode('list');
      resetForm();

      // D3: only the (paginated) order list is refreshed from the server — the
      // quotation link data is form-only and stays in memory.
      // Background refresh — reconciles with server data without blocking UI.
      loadOrders().catch(err =>
        console.error('Background refresh failed:', err)
      );
    } catch (error) {
      console.error('Error saving sale order:', error);
      alert('Error saving sale order: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (so) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    if (so.childIds && so.childIds.length > 0) { alert('Cannot delete order with linked documents'); return; }
    if (!window.confirm(`Delete ${so.docNumber}?`)) return;
    try {
      await deleteTransaction(activeCompany.id, so.id, currentUser.uid);
      // Its quotation can be used again.
      if (so.parentId) {
        setAcceptedQuotations(prev => prev.map(q => (q.id === so.parentId
          ? { ...q, childIds: (q.childIds || []).filter(id => id !== so.id) } : q)));
      }
      await loadOrders();
    } catch (error) { console.error('Error deleting order:', error); alert('Could not delete: ' + (error?.message || 'unknown error')); }
  };

  const handleCancel = async (so) => {
    if (!canCancel) { alert('You do not have permission to cancel.'); return; }
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (so.status === 'Completed' || so.status === 'Cancelled') { alert('This order cannot be cancelled'); return; }
    if (!window.confirm(`Cancel ${so.docNumber}?`)) return;
    try {
      await updateTransactionStatus(activeCompany.id, so.id, 'Cancelled', currentUser.uid);
      await loadOrders();
    } catch (error) { console.error('Error cancelling order:', error); }
  };

  // Manual closing: the order stays as entered (e.g. 100 ordered, 98 delivered)
  // but stops waiting for the rest — it leaves the "Link to SO" lists and the
  // rest shows as closed, not pending. Admin only. Reopen undoes it.
  const handleCloseOrder = async (so) => {
    if (!isAdmin) { alert('Only an admin can close an order.'); return; }
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    const left = orderPendingLines(so);
    const list = left.length
      ? left.slice(0, 12).map(p => `• ${p.label}: ${p.left}${p.unit ? ' ' + p.unit : ''} (ordered ${p.ordered}, delivered ${p.done})`).join('\n') + (left.length > 12 ? `\n…and ${left.length - 12} more` : '')
      : '(nothing is pending)';
    const reason = window.prompt(
      `Close Sale Order ${so.docNumber}?\n\nStill pending — will be closed:\n${list}\n\n` +
      `The order stays as entered; it just stops waiting for the rest.\n\nReason (optional), then OK:`,
      ''
    );
    if (reason === null) return;
    try {
      const updated = await closeOrder(activeCompany.id, so.id, {
        userId: currentUser?.uid || null,
        userName: userProfile?.displayName || userProfile?.fullName || currentUser?.email || null,
        reason,
      });
      if (viewingOrder && viewingOrder.id === so.id) setViewingOrder({ ...so, ...updated, id: so.id });
      await loadOrders();
    } catch (error) {
      console.error('Error closing order:', error);
      alert('Could not close the order: ' + (error?.message || 'unknown error'));
    }
  };

  const handleReopenOrder = async (so) => {
    if (!isAdmin) { alert('Only an admin can reopen an order.'); return; }
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (!window.confirm(`Reopen Sale Order ${so.docNumber}?\n\nIt will show again in "Link to SO" for the quantity still pending.`)) return;
    try {
      const updated = await reopenOrder(activeCompany.id, so.id, { userId: currentUser?.uid || null });
      if (viewingOrder && viewingOrder.id === so.id) setViewingOrder({ ...so, ...updated, id: so.id });
      await loadOrders();
    } catch (error) {
      console.error('Error reopening order:', error);
      alert('Could not reopen the order: ' + (error?.message || 'unknown error'));
    }
  };

  const handlePrint = (so) => {
    const target = so || viewingOrder;
    const cust = customers.find(c => c.id === target?.partyId);
    const enrichedLines = (target?.lines || []).map(l => {
      const it = items.find(i => i.id === l.itemId);
      return { ...l, imageUrl: it?.imageUrl || l.imageUrl || '' };
    });
    const enriched = { ...target, lines: enrichedLines, partyNTN: cust?.ntn || target?.partyNTN || '', partySTRN: cust?.strn || target?.partySTRN || '' };
    printDocument('saleOrder', enriched, activeCompany, printConfig, activeBranch);
  };

  // Keyboard shortcuts
  useDocumentShortcuts({
    onNew: canEdit ? handleCreate : undefined,
    onSave: canEdit ? () => handleSave('Approved') : undefined,
    viewMode,
    saving
  });

  const renderListView = () => (
    <>
      <ActionBar>
        <SearchBox><FiSearch /><input type="text" placeholder="Search by order#, customer, PO#..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} /></SearchBox>
        <FilterGroup>
          <StatusFilterSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="All">All Status</option>
            <option value="Draft">Draft</option>
            <option value="Approved">Approved</option>
            <option value="Partially Delivered">Partially Delivered</option>
            <option value="Completed">Completed</option>
            <option value="Closed">Closed</option>
            <option value="Cancelled">Cancelled</option>
          </StatusFilterSelect>
          {canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> New Order</TranAddButton>}
        </FilterGroup>
      </ActionBar>

      {loading ? <LoadingMessage>Loading sale orders...</LoadingMessage> : filteredOrders.length === 0 ? (
        <TranEmptyState><FiFileText size={48} /><h3>No Sale Orders</h3><p>{orders.length === 0 ? 'Create your first sale order' : 'No orders match your filters'}</p>
          {orders.length === 0 && canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> Create Order</TranAddButton>}</TranEmptyState>
      ) : (
        <TableContainer><SortContext.Provider value={sort.ctxValue}><Table><thead><tr>
          <SortableTh field="docNumber">Order #</SortableTh><SortableTh field="docDate">Date</SortableTh>{showBranchCol && <SortableTh field="branchName">Branch</SortableTh>}<SortableTh field="partyName">Customer</SortableTh><SortableTh field="customerPO">Customer PO</SortableTh>
          <SortableTh field="totalAmount" style={{ textAlign: 'right' }}>Amount</SortableTh><SortableTh field="status" style={{ textAlign: 'center' }}>Status</SortableTh><Th style={{ textAlign: 'center' }}>Actions</Th>
        </tr></thead><tbody>
          {filteredOrders.map(so => (
            <Tr key={so.id}>
              <Td>
                <strong>{so.docNumber}</strong>
                {so.parentId && (
                  <LinkedBadge title={`From ${so.parentDocNumber}`}>
                    <FiLink size={10} /> {so.parentDocNumber}
                  </LinkedBadge>
                )}
                {so.childIds && so.childIds.length > 0 && (
                  <LinkedBadge>
                    <FiLink size={10} /> <LinkedDocNumbers ids={so.childIds} showIcon={false} color="inherit" style={{ fontSize: 'inherit' }} />
                  </LinkedBadge>
                )}
              </Td>
              <Td>{so.docDate}</Td>
              {showBranchCol && <Td>{so.branchName || '-'}</Td>}
              <Td>{so.partyName}</Td>
              <Td>{so.customerPO || '-'}</Td>
              <Td style={{ textAlign: 'right', fontWeight: 500 }}>{activeCompany?.currencyCode || 'PKR'} {formatAmount(so.totalAmount)}</Td>
              <Td style={{ textAlign: 'center' }}><StatusBadge $color={STATUS_COLORS[so.status]}>{so.status}</StatusBadge></Td>
              <Td style={{ textAlign: 'center' }}><ActionGroup>
                <IconBtn title="View" onClick={() => handleView(so)}><FiEye /></IconBtn>
                {canChange && so.status !== 'Cancelled' && so.status !== 'Closed' && (!so.childIds || so.childIds.length === 0) && <IconBtn title="Edit" onClick={() => handleEdit(so)}><FiEdit2 /></IconBtn>}
                <IconBtn title="Print" onClick={() => handlePrint(so)}><FiPrinter /></IconBtn>
                {so.status !== 'Draft' && so.status !== 'Cancelled' && (
                  <SendWhatsAppButton templateKey="saleOrder" doc={so} party={customers.find(c => c.id === so.partyId)} variant="icon" />
                )}
                {canDelete && so.status !== 'Cancelled' && so.status !== 'Closed' && (!so.childIds || so.childIds.length === 0) && <IconBtn title="Delete" $danger onClick={() => handleDelete(so)}><FiTrash2 /></IconBtn>}
                {isAdmin && canEdit && (so.status === 'Approved' || so.status === 'Partially Delivered') && <IconBtn title="Close order (stop waiting for the rest)" onClick={() => handleCloseOrder(so)}><FiLock /></IconBtn>}
                {isAdmin && canEdit && so.status === 'Closed' && <IconBtn title="Reopen order" onClick={() => handleReopenOrder(so)}><FiUnlock /></IconBtn>}
                {canCancel && canEdit && so.status !== 'Completed' && so.status !== 'Cancelled' && so.status !== 'Closed' && <IconBtn title="Cancel" $danger onClick={() => handleCancel(so)}><FiXCircle /></IconBtn>}
              </ActionGroup></Td>
            </Tr>
          ))}
        </tbody></Table></SortContext.Provider></TableContainer>
      )}
      <DocListPagination {...pagination} />

      <SummaryBar><span>Total: <strong>{listFiltering ? filteredOrders.length : pagination.totalCount}</strong> order(s)</span>
        <span>Draft: <strong>{listSummary.counts.Draft || 0}</strong> | Approved: <strong>{listSummary.counts.Approved || 0}</strong> | Completed: <strong>{listSummary.counts.Completed || 0}</strong></span>
      </SummaryBar>
    </>
  );

  const renderViewMode = () => {
    if (!viewingOrder) return null; const so = viewingOrder;
    return (<>
      <FormHeader>
        <BackButton onClick={() => { setViewMode('list'); setViewingOrder(null); }}><FiArrowLeft /> Back to List</BackButton>
        <FormTitle>
          {so.docNumber}
          <StatusBadge $color={STATUS_COLORS[so.status]} style={{ marginLeft: 12 }}>{so.status}</StatusBadge>
          {so.parentId && (
            <LinkedBadge style={{ marginLeft: 8 }} title={`From ${so.parentDocNumber}`}>
              <FiLink size={12} /> {so.parentDocNumber}
            </LinkedBadge>
          )}
          {so.childIds && so.childIds.length > 0 && (
            <LinkedBadge style={{ marginLeft: 8 }}>
              <FiLink size={12} /> <LinkedDocNumbers ids={so.childIds} max={0} showIcon={false} color="inherit" style={{ fontSize: 'inherit' }} />
            </LinkedBadge>
          )}
        </FormTitle>
        <FormActions>
          {canChange && so.status !== 'Cancelled' && so.status !== 'Closed' && (!so.childIds || so.childIds.length === 0) && <SaveButton onClick={() => handleEdit(so)}><FiEdit2 /> Edit</SaveButton>}
          {isAdmin && canEdit && (so.status === 'Approved' || so.status === 'Partially Delivered') && <SaveButton $secondary onClick={() => handleCloseOrder(so)}><FiLock /> Close Order</SaveButton>}
          {isAdmin && canEdit && so.status === 'Closed' && <SaveButton $secondary onClick={() => handleReopenOrder(so)}><FiUnlock /> Reopen</SaveButton>}
          <SaveButton $secondary onClick={() => handlePrint(so)}><FiPrinter /> Print</SaveButton>
        </FormActions>
      </FormHeader>
      {so.status === 'Closed' && <ClosedOrderNote order={so} doneLabel="delivered" />}
      <FormCard><SectionTitle>Order Details</SectionTitle>
        <InfoGrid>
          <InfoItem><label>Order Number</label><span>{so.docNumber}</span></InfoItem>
          <InfoItem><label>Date</label><span>{so.docDate}</span></InfoItem>
          <InfoItem><label>Customer</label><span>{so.partyName}</span></InfoItem>
          <InfoItem><label>Customer PO</label><span>{so.customerPO || '-'}</span></InfoItem>
          <InfoItem><label>Quotation Ref</label><span>{so.parentDocNumber || '-'}</span></InfoItem>
          <InfoItem><label>Delivery Date</label><span>{so.deliveryDate || '-'}</span></InfoItem>
          <InfoItem><label>Delivery Address</label><span>{so.deliveryAddress || '-'}</span></InfoItem>
          <InfoItem><label>Currency</label><span>{activeCompany?.currencyCode || 'PKR'}</span></InfoItem>
          <InfoItem><label>Employee</label><span>{so.employeeName || '-'}</span></InfoItem>
          <InfoItem><label>Job</label><span>{so.jobName || '-'}</span></InfoItem>
          <InfoItem><label>Location</label><span>{so.locationName || '-'}</span></InfoItem>
        </InfoGrid>
      </FormCard>
      <FormCard><SectionTitle>Line Items</SectionTitle>
        <TableContainer><Table><thead><tr><Th>#</Th><Th>Description</Th><Th style={{ textAlign: 'right' }}>Qty</Th><Th style={{ textAlign: 'right' }}>Unit Price</Th><Th style={{ textAlign: 'right' }}>Disc %</Th><Th style={{ textAlign: 'right' }}>Tax</Th><Th style={{ textAlign: 'right' }}>Total</Th></tr></thead>
          <tbody>{(so.lines || []).map((line, i) => (
            <Tr key={i}><Td>{i + 1}</Td><Td>{line.description}</Td><Td style={{ textAlign: 'right' }}>{line.quantity}{line.unitOfMeasure ? <span style={{ fontSize: '11px', color: '#64748B', marginLeft: '4px' }}>{line.unitOfMeasure}</span> : ''}<TwoUnitNote line={line} /></Td><Td style={{ textAlign: 'right' }}>{formatAmount(line.unitPrice)}</Td><Td style={{ textAlign: 'right' }}>{line.discountPercent || 0}%</Td><Td style={{ textAlign: 'right' }}>{line.taxable ? formatAmount(line.taxAmount) : '-'}</Td><Td style={{ textAlign: 'right' }}>{formatAmount(line.lineTotal)}</Td></Tr>
          ))}</tbody></Table></TableContainer>
        <TotalsSection><TotalsTable><tbody>
          <tr><td>Subtotal</td><td>{formatAmount(so.subtotal)}</td></tr>
          {so.discountAmount > 0 && <tr><td>Discount</td><td>-{formatAmount(so.discountAmount)}</td></tr>}
          <tr><td>Tax</td><td>{formatAmount(so.taxAmount)}</td></tr>
          <TotalRow><td>Total ({activeCompany?.currencyCode || 'PKR'})</td><td>{formatAmount(so.totalAmount)}</td></TotalRow>
        </tbody></TotalsTable></TotalsSection>
      </FormCard>
      {so.termsConditions && <FormCard><SectionTitle>Terms & Conditions</SectionTitle><NotesText>{so.termsConditions}</NotesText></FormCard>}
      {so.notes && <FormCard><SectionTitle>Notes</SectionTitle><NotesText>{so.notes}</NotesText></FormCard>}
      <AttachmentPanel transactionId={so?.id} attachment={so?.attachment} canEdit={false} />
    </>);
  };

  const renderFormView = () => {
    // A quotation already made into a Sale Order is not offered again (only
    // the order being edited keeps its own quotation).
    const customerQuotations = acceptedQuotations.filter(q =>
      (!formData.partyId || q.partyId === formData.partyId)
      && (!(q.childIds && q.childIds.length > 0) || (editingOrder && q.childIds.includes(editingOrder.id)))
      // only this branch's quotations (see utils/branchLink.js)
      && inActiveBranch(q, activeBranch, formData.parentId));
    return (<>
      <FormHeader>
        <BackButton onClick={() => { setViewMode('list'); resetForm(); }}><FiArrowLeft /> Back to List</BackButton>
        <FormTitle>{editingOrder ? `Edit: ${editingOrder.docNumber}` : 'New Sale Order'}</FormTitle>
        <FormActions>
          <SaveButton $secondary onClick={() => { setViewMode('list'); resetForm(); }}><FiX /> Cancel</SaveButton>
          {canEdit && <SaveButton onClick={() => handleSave('Draft')} disabled={saving}><FiSave /> {saving ? 'Saving...' : 'Save Draft'}</SaveButton>}
          {canEdit && (formData.status === 'Draft' || !editingOrder) && (
            <SaveButton $approve onClick={() => handleSave('Approved')} disabled={saving}><FiCheck /> Approve</SaveButton>
          )}
        </FormActions>
      </FormHeader>
      <FormCard><SectionTitle>Order Details</SectionTitle>
        <FormGrid>
          <FormGroup><Label>Order Number</Label><Input value={editingOrder?.docNumber || 'Auto-generated'} disabled /></FormGroup>
          <FormGroup><Label>Order Date *</Label><Input type="date" value={formData.docDate} onChange={e => handleHeaderChange('docDate', e.target.value)} /></FormGroup>
          <FormGroup><Label>Status</Label><Input value={formData.status} disabled /></FormGroup>
          <FormGroup><Label>Link to Quotation</Label>
            <Select value={formData.parentId} onChange={e => handleQuotationChange(e.target.value)}>
              <option value="">-- No Quotation --</option>
              {customerQuotations.map(q => (<option key={q.id} value={q.id}>{q.docNumber} - {q.partyName}</option>))}
            </Select>
          </FormGroup>
          <FormGroup $span2><Label>Customer *</Label>
            <CustomerSelect customers={customers} selectedCustomerId={formData.partyId} onChange={handleCustomerChange} disabled={!!formData.parentId} balanceOnDemand />
          </FormGroup>
          <FormGroup><Label>Customer PO</Label><Input value={formData.customerPO} onChange={e => handleHeaderChange('customerPO', e.target.value)} placeholder="Customer's PO#" /></FormGroup>
          <FormGroup><Label>Delivery Date</Label><Input type="date" value={formData.deliveryDate} onChange={e => handleHeaderChange('deliveryDate', e.target.value)} /></FormGroup>
          <FormGroup $span2><Label>Delivery Address</Label><Input value={formData.deliveryAddress} onChange={e => handleHeaderChange('deliveryAddress', e.target.value)} placeholder="Delivery location" /></FormGroup>
          <FormGroup><Label>Currency</Label><Input value={activeCompany?.currencyCode || 'PKR'} disabled /></FormGroup>
          <FormGroup>
            <Label>Employee</Label>
            <Select value={formData.employeeId} onChange={e => {
              const emp = employees.find(em => em.id === e.target.value);
              handleHeaderChange('employeeId', e.target.value);
              handleHeaderChange('employeeName', emp ? `${emp.firstName || ''} ${emp.lastName || ''}`.trim() : '');
            }}>
              <option value="">-- Select Employee --</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>{emp.employeeCode ? emp.employeeCode + ' - ' : ''}{emp.firstName} {emp.lastName}</option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup>
            <Label>Job</Label>
            <Select value={formData.jobId} onChange={e => {
              const job = jobs.find(j => j.id === e.target.value);
              handleHeaderChange('jobId', e.target.value);
              handleHeaderChange('jobName', job?.jobName || '');
            }}>
              <option value="">-- Select Job --</option>
              {jobs.map(job => (
                <option key={job.id} value={job.id}>{job.jobCode ? job.jobCode + ' - ' : ''}{job.jobName}</option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup>
            <Label>{reqLabel(activeCompany, 'Location')}</Label>
            <Select value={formData.locationId} onChange={e => {
              const loc = locations.find(l => l.id === e.target.value);
              handleHeaderChange('locationId', e.target.value);
              handleHeaderChange('locationName', loc?.locationName || '');
            }}>
              <option value="">-- Select Location --</option>
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.locationName}</option>
              ))}
            </Select>
          </FormGroup>
        </FormGrid>
      </FormCard>
      <FormCard>
        <LineItemsHeader><SectionTitle style={{ margin: 0 }}>Line Items</SectionTitle>
          {!formData.parentId && <AddLineButton onClick={addLine}><FiPlus /> Add Row</AddLineButton>}
        </LineItemsHeader>
        <LineTableContainer><LineTable><thead><tr>
          <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}>#</LineTh><LineTh style={{ width: 220, minWidth: 220, maxWidth: 220 }}>Item</LineTh><LineTh style={{ width: 260, minWidth: 260, maxWidth: 260 }}>Description</LineTh>
          {lineItems.some(l => l.hsCode) && <LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>HS Code</LineTh>}
          <LineTh style={{ width: 120, minWidth: 120, maxWidth: 120 }}>Qty</LineTh><LineTh style={{ width: 120, minWidth: 120, maxWidth: 120 }}>Price Type</LineTh><LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>Unit Price</LineTh><LineTh style={{ width: 100, minWidth: 100, maxWidth: 100 }}>Disc % / Amt</LineTh>
          <LineTh style={{ width: 80, minWidth: 80, maxWidth: 80 }}>Tax?</LineTh><LineTh style={{ width: 100, minWidth: 100, maxWidth: 100 }}>Tax %</LineTh><LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>Total</LineTh><LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}></LineTh>
        </tr></thead><tbody>
          {lineItems.map((line, index) => {
            const calc = calculateLine(line);
            return (<tr key={index}>
              <LineTd style={{ textAlign: 'center', color: colors.text.secondary }}>{index + 1}</LineTd>
              <LineTd><ItemSelect ref={(el) => { lineItemRefs.current[index] = el; }} items={filterItemsByPricing(items, customerPricing)} selectedItemId={line.itemId} onChange={(itemId) => handleLineChange(index, 'itemId', itemId)} disabled={!!formData.parentId} stockBalances={stockBalances} stockOnDemand onRequestStock={requestItemStock} /></LineTd>
              <LineTd><LineInput value={line.description} onChange={e => handleLineChange(index, 'description', e.target.value)} placeholder="Description" disabled={!!formData.parentId} /></LineTd>
              {lineItems.some(l => l.hsCode) && <LineTd><LineInput value={line.hsCode || ''} onChange={e => handleLineChange(index, 'hsCode', e.target.value)} placeholder="HS Code" disabled={!!formData.parentId} /></LineTd>}
              <LineTd>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <LineInput type="number" min="0" value={line.quantity} onChange={e => handleLineChange(index, 'quantity', Number(e.target.value))} style={{ textAlign: 'right' }} />
                  <LineUnitPicker
                    item={items.find(i => i.id === line.itemId)}
                    line={line}
                    onChange={(uc) => handleLineChange(index, 'unitCode', uc)}
                    disabled={!!formData.parentId}
                  />
                </div>
                <TwoUnitQtyBox item={items.find(i => i.id === line.itemId)} unitCode={line.unitCode || line.unitOfMeasure}
                  quantity={line.quantity} hasSize={line.hasSize}
                  onChange={(q) => handleLineChange(index, 'quantity', q)} />
              </LineTd>
              <LineTd><Select value={line.priceType || 'Retail'} onChange={e => handleLineChange(index, 'priceType', e.target.value)} style={{ padding: '4px 6px', fontSize: '12px', minWidth: 80 }}><option value="Retail">Retail</option><option value="Wholesale">Wholesale</option><option value="Dealer">Dealer</option></Select></LineTd>
              <LineTd><LineInput type="number" min="0" step="0.01" value={line.unitPrice} onChange={e => handleLineChange(index, 'unitPrice', Number(e.target.value))} onBlur={() => handlePriceBlur(index)} style={{ textAlign: 'right' }} /></LineTd>
              <LineTd>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <LineInput type="number" min="0" max="100" step="0.01" value={line.discountPercent}
                    onChange={e => handleLineChange(index, 'discountPercent', Number(e.target.value))} style={{ textAlign: 'right' }} title="Discount %" placeholder="%" />
                  <LineInput type="number" min="0" step="0.01" value={calc.discountAmount || 0}
                    onChange={e => handleLineChange(index, 'discountAmountInput', Number(e.target.value))} style={{ textAlign: 'right' }} title="Discount amount" placeholder="Amt" />
                </div>
              </LineTd>
              <LineTd style={{ textAlign: 'center' }}><input type="checkbox" checked={line.taxable} onChange={e => handleLineChange(index, 'taxable', e.target.checked)} onKeyDown={(e) => { if (e.key === 'Enter' && !line.taxable) handleLastFieldEnter(e, index); }} /></LineTd>
              <LineTd><LineInput type="number" min="0" max="100" value={line.taxRate} onChange={e => handleLineChange(index, 'taxRate', Number(e.target.value))} onKeyDown={(e) => handleLastFieldEnter(e, index)} disabled={!line.taxable} style={{ textAlign: 'right', opacity: line.taxable ? 1 : 0.4 }} /></LineTd>
              <LineTd style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(calc.lineTotal)}</LineTd>
              <LineTd style={{ textAlign: 'center' }}>{!formData.parentId && lineItems.length > 1 && <RemoveLineBtn onClick={() => removeLine(index)} title="Remove"><FiX /></RemoveLineBtn>}</LineTd>
            </tr>);
          })}
        </tbody></LineTable></LineTableContainer>
        <TotalsSection><TotalsTable><tbody>
          <tr><td>Subtotal</td><td>{formatAmount(totals.subtotal)}</td></tr>
          <tr><td>Discount</td><td><DiscountInput type="number" min="0" step="0.01" value={formData.discountAmount} onChange={e => handleHeaderChange('discountAmount', Number(e.target.value))} /></td></tr>
          <tr><td>Tax</td><td>{formatAmount(totals.taxAmount)}</td></tr>
          <TotalRow><td>Total ({activeCompany?.currencyCode || 'PKR'})</td><td>{formatAmount(totals.totalAmount)}</td></TotalRow>
        </tbody></TotalsTable></TotalsSection>
      </FormCard>
      <FormCard><SectionTitle>Additional Information</SectionTitle><FormGrid>
        <FormGroup $span2><Label>Terms & Conditions</Label><TextArea value={formData.termsConditions} onChange={e => handleHeaderChange('termsConditions', e.target.value)} placeholder="Terms and conditions..." rows={3} /></FormGroup>
        <FormGroup $span2><Label>Notes</Label><TextArea value={formData.notes} onChange={e => handleHeaderChange('notes', e.target.value)} placeholder="Internal notes..." rows={3} /></FormGroup>
      </FormGrid></FormCard>

      <AttachmentPanel
        transactionId={editingOrder?.id}
        attachment={editingOrder?.attachment}
        canEdit={canEdit}
        onChange={({ attachment }) => setEditingOrder(prev => prev ? { ...prev, attachment } : prev)}
      />
    </>);
  };

  return (
    <PageLayout pageTitle="Sale Order" breadcrumb="Sales / Sale Order">
      {viewMode === 'list' && renderListView()}
      {viewMode === 'form' && renderFormView()}
      {viewMode === 'view' && renderViewMode()}
    </PageLayout>
  );
};

// ============ UNIQUE STYLED COMPONENTS ============
const LinkedBadge = styled.span`display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:10px;font-size:10px;font-weight:500;color:${colors.info.main};background:${colors.info.main}15;margin-left:6px;`;

export default SaleOrder;
